import { Suspense, useState } from "react";
import {
  ActionIcon,
  Button,
  Container,
  Loader,
  Select,
  Space,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconDownload, IconSearch, IconTrash } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useListEvents } from "../hooks/useListEvents.ts";
import { useRegistrationsByEvent } from "../hooks/useRegistrationsByEvent.ts";
import { useSearchRegistrations } from "../hooks/useSearchRegistrations.ts";
import { useRecentRegistrations } from "../hooks/useRecentRegistrations.ts";
import { useStatistics } from "../hooks/useStatistics.ts";
import { useDeleteRegistration } from "../hooks/useDeleteRegistration.ts";

const maskIban = (iban: string) => {
  if (iban.length <= 4) return iban;
  return "****" + iban.slice(-4);
};

interface RegistrationRow {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  iban: string;
  registrationDate: string;
  eventTitle?: string;
}

const RegistrationTableContent = ({
  registrations,
  showEvent,
  emptyMessage = "Keine Anmeldungen gefunden.",
}: {
  registrations: RegistrationRow[];
  showEvent: boolean;
  emptyMessage?: string;
}) => {
  const deleteMutation = useDeleteRegistration();

  const handleDelete = (registrationId: string, name: string) => {
    modals.openConfirmModal({
      title: "Anmeldung löschen",
      centered: true,
      children: (
        <Text>
          Anmeldung von <strong>{name}</strong> wirklich löschen?
        </Text>
      ),
      labels: { confirm: "Löschen", cancel: "Abbrechen" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(registrationId),
    });
  };

  if (registrations.length === 0) {
    return <Text c="dimmed">{emptyMessage}</Text>;
  }

  return (
    <Table.ScrollContainer minWidth={500}>
      <Table striped highlightOnHover horizontalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>E-Mail</Table.Th>
            <Table.Th>IBAN</Table.Th>
            {showEvent && <Table.Th>Event</Table.Th>}
            <Table.Th>Anmeldedatum</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {registrations.map((r) => (
            <Table.Tr key={r.id}>
              <Table.Td>
                {r.firstname} {r.lastname}
              </Table.Td>
              <Table.Td>{r.email}</Table.Td>
              <Table.Td>{maskIban(r.iban)}</Table.Td>
              {showEvent && <Table.Td>{r.eventTitle}</Table.Td>}
              <Table.Td>
                {DateTime.fromISO(r.registrationDate)
                  .setZone("Europe/Berlin")
                  .setLocale("de")
                  .toFormat("dd.MM.yyyy, HH:mm")}
              </Table.Td>
              <Table.Td>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    handleDelete(r.id, `${r.firstname} ${r.lastname}`)
                  }
                  loading={
                    deleteMutation.isPending &&
                    deleteMutation.variables === r.id
                  }
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

const EventRegistrations = ({ eventId }: { eventId: string }) => {
  const { data } = useRegistrationsByEvent(eventId);
  return (
    <RegistrationTableContent
      registrations={data.registrations}
      showEvent={false}
    />
  );
};

const RecentRegistrations = () => {
  const { data, isLoading } = useRecentRegistrations();

  if (isLoading) return <Loader />;
  if (!data) return null;

  return (
    <RegistrationTableContent
      registrations={data.registrations}
      showEvent={true}
      emptyMessage="Keine Anmeldungen innerhalb der letzten 60 Minuten."
    />
  );
};

const SearchResults = ({ query }: { query: string }) => {
  const { data, isLoading } = useSearchRegistrations(query);

  if (isLoading) return <Loader />;
  if (!data) return null;

  return (
    <RegistrationTableContent
      registrations={data.registrations}
      showEvent={true}
    />
  );
};

const Statistics = () => {
  const { data, isLoading } = useStatistics();

  if (isLoading) return <Loader />;
  if (!data) return null;

  return (
    <Table.ScrollContainer minWidth={400}>
      <Table striped highlightOnHover horizontalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Event</Table.Th>
            <Table.Th>Anmeldungen</Table.Th>
            <Table.Th>Freie Plätze</Table.Th>
            <Table.Th>Anmeldeschluss</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.statistics.map((s) => (
            <Table.Tr key={s.title}>
              <Table.Td>{s.title}</Table.Td>
              <Table.Td>{s.numberOfRegistrations}</Table.Td>
              <Table.Td>{s.freeSeats}</Table.Td>
              <Table.Td>
                {DateTime.fromISO(s.endOfRegistration)
                  .setZone("Europe/Berlin")
                  .setLocale("de")
                  .toFormat("dd.MM.yyyy, HH:mm")}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

export const Admin = () => {
  const { data } = useListEvents();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const eventOptions = data.events.map((e) => ({
    value: e.id,
    label: `${e.title} (${DateTime.fromISO(e.startDate).setZone("Europe/Berlin").setLocale("de").toFormat("dd.MM.yyyy")})`,
  }));

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>Anmeldungen verwalten</Title>
        <Tabs defaultValue="search">
          <Tabs.List
            style={{
              flexWrap: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            <Tabs.Tab value="search">Suche</Tabs.Tab>
            <Tabs.Tab value="event">Events</Tabs.Tab>
            <Tabs.Tab value="recent">Neue Anmeldungen</Tabs.Tab>
            <Tabs.Tab value="statistics">Statistiken</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="search" pt="md">
            <Stack>
              <TextInput
                label="Suche"
                placeholder="Name, E-Mail oder IBAN..."
                leftSection={<IconSearch size={16} />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
              />
              <Space h="sm" />
              {debouncedSearch.length >= 2 && (
                <SearchResults query={debouncedSearch} />
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="event" pt="md">
            <Stack>
              <Select
                label="Event auswählen"
                placeholder="Event wählen..."
                data={eventOptions}
                value={selectedEventId}
                onChange={setSelectedEventId}
                searchable
              />
              {selectedEventId && (
                <Button
                  component="a"
                  href={`${import.meta.env.VITE_APP_API_BASE_URI}/v1/registration.read/export.csv?eventId=${selectedEventId}`}
                  variant="light"
                  leftSection={<IconDownload size={16} />}
                  style={{ alignSelf: "flex-start" }}
                >
                  CSV-Export
                </Button>
              )}
              <Space h="sm" />
              {selectedEventId && (
                <Suspense fallback={<Loader />}>
                  <EventRegistrations eventId={selectedEventId} />
                </Suspense>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="recent" pt="md">
            <RecentRegistrations />
          </Tabs.Panel>

          <Tabs.Panel value="statistics" pt="md">
            <Statistics />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};
